import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import * as api from '@/lib/api'
import type { AdminScheduleEvent, AdminGroup } from '@/lib/api'

interface FormData {
  group_slug: string
  name: string
  date: string
  location: string
  time: string
  badge: string
  cheki_url: string
}

const blank: FormData = {
  group_slug: '', name: '', date: '', location: '', time: '', badge: 'HANDSHAKE', cheki_url: '',
}

function toFormData(e: AdminScheduleEvent): FormData {
  return {
    group_slug: e.group_slug, name: e.name, date: e.date ?? '',
    location: e.location, time: e.time, badge: e.badge,
    cheki_url: e.cheki_url ?? '',
  }
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900 overflow-hidden">
      <div className="px-5 py-3 border-b border-zinc-800">
        <h2 className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">{title}</h2>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  )
}

export default function ScheduleFormPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { token, isGroupManager, managedGroupSlug } = useAuth()

  const isEdit = Boolean(id)

  const [form, setForm] = useState<FormData>(blank)
  const [groups, setGroups] = useState<AdminGroup[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!token) return

    const fetchGroups = api.getAdminGroups(token)

    if (isEdit) {
      const stateEvent = (location.state as { event?: AdminScheduleEvent } | null)?.event
      if (stateEvent) {
        fetchGroups.then((groupList) => {
          setGroups(groupList)
          setForm(toFormData(stateEvent))
          setLoading(false)
        })
      } else {
        Promise.all([fetchGroups, api.getAdminSchedule(token)]).then(([groupList, events]) => {
          setGroups(groupList)
          const found = events.find((e) => e.id === id)
          if (found) setForm(toFormData(found))
          setLoading(false)
        })
      }
    } else {
      fetchGroups.then((groupList) => {
        setGroups(groupList)
        if (isGroupManager && managedGroupSlug) {
          setForm((prev) => ({ ...prev, group_slug: managedGroupSlug }))
        }
        setLoading(false)
      })
    }
  }, [id, isEdit, token, location.state, isGroupManager, managedGroupSlug])

  const set = (field: keyof FormData, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!token) return
    setSaving(true)
    try {
      const payload = { ...form, date: form.date || null, cheki_url: form.cheki_url || null }
      if (isEdit && id) {
        const updated = await api.updateSchedule(token, id, payload)
        navigate(`/schedule/${id}`, { replace: true, state: { event: updated } })
      } else {
        await api.createSchedule(token, payload)
        navigate(-1)
      }
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  const inp = 'w-full bg-zinc-800 text-white rounded-lg px-3 py-2 text-sm border border-zinc-700 focus:outline-none focus:border-zinc-500 placeholder:text-zinc-600'
  const lbl = 'block text-xs text-zinc-400 mb-1.5'

  if (loading) {
    return <div className="p-6"><p className="text-zinc-500 text-sm">Loading…</p></div>
  }

  return (
    <div className="p-6">
      <form onSubmit={handleSubmit}>
        {/* Sticky top bar */}
        <div className="sticky top-0 z-10 bg-zinc-950 flex items-center justify-between pb-4 mb-6 border-b border-zinc-800">
          <div>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300 transition-colors mb-3"
            >
              <ChevronLeft size={14} />
              Kembali
            </button>
            <h1 className="text-xl font-bold text-white">
              {isEdit ? 'Edit Event' : 'New Event'}
            </h1>
            {isEdit && <p className="text-zinc-500 text-sm mt-1">{form.name}</p>}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-5 py-2 text-sm text-zinc-400 hover:text-white transition-colors rounded-lg hover:bg-zinc-800"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 text-sm font-semibold bg-yellow-500 hover:bg-yellow-400 text-black rounded-lg transition-colors disabled:opacity-50"
            >
              {saving ? 'Menyimpan…' : isEdit ? 'Simpan Perubahan' : 'Buat Event'}
            </button>
          </div>
        </div>
        <div className="max-w-3xl mx-auto space-y-4">

        {/* ── Informasi Event ──────────────────────────── */}
        <Section title="Informasi Event">
          <div>
            <label className={lbl}>Nama Event <span className="text-red-400">*</span></label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              required
              placeholder="Judul atau nama acara"
              className={inp}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={lbl}>Group <span className="text-red-400">*</span></label>
              {isGroupManager ? (
                <input
                  type="text"
                  value={form.group_slug}
                  disabled
                  className={inp + ' opacity-40 cursor-not-allowed'}
                />
              ) : (
                <select
                  value={form.group_slug}
                  onChange={(e) => set('group_slug', e.target.value)}
                  required
                  className={inp}
                >
                  <option value="">Pilih group…</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.slug}>{g.name}</option>
                  ))}
                </select>
              )}
            </div>
            <div>
              <label className={lbl}>Badge</label>
              <input
                type="text"
                value={form.badge}
                onChange={(e) => set('badge', e.target.value)}
                placeholder="HANDSHAKE / CONCERT / FAN MEETING"
                className={inp}
              />
            </div>
          </div>
        </Section>

        {/* ── Waktu & Lokasi ───────────────────────────── */}
        <Section title="Waktu & Lokasi">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={lbl}>Tanggal <span className="text-zinc-600">(opsional)</span></label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => set('date', e.target.value)}
                className={inp + ' [color-scheme:dark]'}
              />
              <p className="text-[11px] text-zinc-600 mt-1.5">Kosongkan jika tanggal belum ditentukan (TBA)</p>
            </div>
            <div>
              <label className={lbl}>Waktu</label>
              <input
                type="time"
                value={form.time}
                onChange={(e) => set('time', e.target.value)}
                className={inp + ' [color-scheme:dark]'}
              />
            </div>
          </div>

          <div>
            <label className={lbl}>Lokasi</label>
            <input
              type="text"
              value={form.location}
              onChange={(e) => set('location', e.target.value)}
              placeholder="Nama venue, Kota"
              className={inp}
            />
          </div>
        </Section>

        {/* ── Link ────────────────────────────────────── */}
        <Section title="Link">
          <div>
            <label className={lbl}>Link Cheki</label>
            <input
              type="url"
              value={form.cheki_url}
              onChange={(e) => set('cheki_url', e.target.value)}
              placeholder="https://..."
              className={inp}
            />
            <p className="text-[11px] text-zinc-600 mt-1.5">
              URL pembelian cheki atau tiket (opsional)
            </p>
          </div>
        </Section>

        </div>
      </form>
    </div>
  )
}
